import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Drawer, Menu } from "antd";
import {
  CarOutlined,
  ClusterOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  EnvironmentOutlined,
  FrownOutlined,
  FundOutlined,
  HomeOutlined,
  RadarChartOutlined,
  ToolOutlined,
  UnorderedListOutlined,
  UsergroupAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { MAIN_BAND_ID } from "../constants";
import { useResourceContext } from "../domain/common/resource/useResourceContext";
import { useAuthContext } from "../domain/auth/context/useAuthContext";
import newBadge from "../assets/new-badge.svg";

const newVehicleLabel = <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>킥보드/자전거<img src={newBadge} width="22" height="22" alt="NEW" /></span>;

function Sidebar({ open, onClose }) {
  const history = useHistory();
  const { auth, hasPermission } = useAuthContext();
  const { R } = useResourceContext();
  const [menuItems, setMenuItems] = useState([]);

  const getItem = useCallback((label, key, icon, children) => {
    return {
      key,
      icon,
      children,
      label,
    };
  }, []);

  const isMainBand = useMemo(() => {
    return auth?.band?.id === MAIN_BAND_ID;
  }, [auth?.band?.id]);
  
  const checkPermission = useCallback((permission) => {
    return hasPermission(permission);
  }, [hasPermission]);

  useEffect(() => {
    if (!auth) return;
    
    const items = [
      // 대시보드 메뉴 아이템
      createDashboardMenuItem(),
      
      // 미지급 리스트 메뉴 아이템
      isMainBand && getItem(R.text.list_of_unpaid_payments, "not-paid", <CreditCardOutlined />),
      
      // 차량 메뉴 아이템
      createVehicleMenuItem(),
      
      // 시냅스/조바 메뉴 아이템
      createSynapseMenuItem(),
      
      // 견인관리 메뉴 아이템
      checkPermission("TowMenuVisible") && 
        getItem('견인관리', "tow", <FrownOutlined />, [
          getItem('견인등록', "tow/register")
        ]),
      
      // 작업관리 메뉴 아이템
      checkPermission("TaskManagementVisible") &&
        getItem("작업관리", "task", <ToolOutlined />),
      
      // 라이드 메뉴 아이템
      checkPermission("RideMenuVisible") &&
        getItem(R.text.ride, "ride", <DashboardOutlined />),
      
      // 지오펜스 메뉴 아이템
      createGeofenceMenuItem(),
      
      // 드롭존 메뉴 아이템
      createDropzoneMenuItem(),
      
      // 입출고 메뉴 아이템
      createStorageMenuItem(),
      
      // 유저 메뉴 아이템
      createUserMenuItem(),
      
      // 계정 메뉴 아이템
      createAccountMenuItem(),
      
      // 기타 메뉴 아이템
      createEtcMenuItem(),
    ].filter(Boolean);
    
    setMenuItems(items);
    
    function createDashboardMenuItem() {
      if (checkPermission("DashboardMenuVisible")) {
        return getItem('대시보드', "dashboardCollapse", <FundOutlined />, [
          getItem('대시보드', "v3/dashboard"),
          checkPermission("DataDashboardVisible") && getItem('신규대시보드', "sales/dashboard"),
        ].filter(Boolean));
      } else if (checkPermission("OnlyVC")) {
        return getItem(R.text.dashboard, "dashboard", <FundOutlined />);
      }
      return null;
    }
    
    function createVehicleMenuItem() {
      if (checkPermission("ScootMenuVisible")) {
        return getItem(R.text.vehicle, "vehiclesCollapse", <CarOutlined />, [
          getItem(newVehicleLabel, "scoot-new"),
          getItem(`${R.text.e_scooters_e_bikes} (기존 버전)`, "scoot"),
          getItem(R.text.e_moped, "moped"),
          isMainBand && getItem(R.text.duration_of_use, "used"),
          checkPermission("HistoryMenuVisible") && getItem(R.text.total_history, "scoot/history"),
          checkPermission("HistoryMenuVisible") && getItem(R.text.qr_code_modification_history, "scoot/history-qr"),
        ].filter(Boolean));
      } else if (checkPermission("OnlyVC")) {
        return getItem(R.text.vehicle, "vehiclesCollapse", <CarOutlined />, [
          getItem(newVehicleLabel, "scoot-new"),
          getItem(`${R.text.e_scooters_e_bikes} (기존 버전)`, "scoot"),
          getItem(R.text.e_moped, "moped"),
          checkPermission("HistoryMenuVisible") && getItem(R.text.total_history, "scoot/history"),
        ].filter(Boolean));
      }
      return null;
    }
    
    function createSynapseMenuItem() {
      if (checkPermission("ZobaMenuEnable")) {
        return getItem("운영", "synapseCollapse", <ClusterOutlined />, [
          checkPermission("ZobaJobEnable") && checkPermission("ZobaRecommendedJobMenuVisible") &&
            getItem("시냅스 추천작업", "synapse/recommended-job"),
          getItem("캠프관리", "synapse/camp"),
          isMainBand && getItem("수거구역", "synapse/collect"),
          isMainBand && getItem("수거구역 리스트", "synapse/collect-list"),
          isMainBand && getItem("배치구역", "synapse/deployment"),
          isMainBand && getItem("배치구역 리스트", "synapse/deployment-list"),
        ].filter(Boolean));
      }
      return null;
    }
    
    function createGeofenceMenuItem() {
      if (checkPermission("AreaMenuVisible") || checkPermission("makeGeofenceMenuVisible")) {
        return getItem(R.text.geofence, "geofence", <RadarChartOutlined />, [
          checkPermission("AreaMenuVisible") && getItem(R.text.dummy_geofence_list, "area/operation"),
          getItem(R.text.collection_geofence_list, "area/pick"),
          getItem(R.text.collection_geofence_map, "area/collector-area"),
          getItem("콜렉터 구역 맵", "area/collector-map"),
          getItem("신규 콜랙터 구역맵", "area/new-collector-map"),
        ].filter(Boolean));
      }
      return null;
    }
    
    function createDropzoneMenuItem() {
      if (checkPermission("AllocatePointMenuVisible")) {
        return getItem(R.text.dropzone, "dropzoneCollapse", <EnvironmentOutlined />, [
          getItem(R.text.dropzone_map, "dropzone/map"),
          getItem(R.text.e_scooter_dropzone_list, "map/dropzone?zoneType=Scooter"),
          getItem(R.text.e_bike_dropzone_list, "map/dropzone?zoneType=Bike"),
          getItem(R.text.e_moped_dropzone_list, "map/dropzone?zoneType=Moped"),
          getItem("일반자전거 배치포인트 리스트", "map/dropzone?zoneType=NormalBike"),
        ]);
      }
      return null;
    }
    
    function createStorageMenuItem() {
      if (checkPermission("ScootStorageMenuVisible")) {
        return getItem(R.text.in_out, "storage", <HomeOutlined />, [
          getItem(R.text.in_out_status, "storage/status"),
          getItem(R.text.in_out_history, "storage/history"),
        ]);
      }
      return null;
    }
    
    function createUserMenuItem() {
      if (checkPermission("UserMenuVisible")) {
        return getItem(R.text.user, "userCollapse", <UserOutlined />, [
          getItem("유저 검색", "user"),
        ]);
      }
      return null;
    }
    
    function createAccountMenuItem() {
      const items = getItem(R.text.account, "account", <UsergroupAddOutlined />, [
        ...(checkPermission("AdminAccountMenuVisible") ? [getItem(R.text.admin_user_list, "permission/admin")] : []),
        ...(checkPermission("CollectorAccountMenuVisible") ? [getItem(R.text.collector_user_list, "permission/collector")] : []),
      ]);
      return items.children.length > 0 ? items : null;
    }
    
    function createEtcMenuItem() {
      if (checkPermission("EtcMenuVisible")) {
        return getItem(R.text.etc, "etc", <UnorderedListOutlined />, [
          getItem(R.text.region_list, "band/create"),
          getItem(R.text.warehouse_list, "band/warehouse"),
          getItem(R.text.vendor_manage, "collectors"),
        ]);
      }
      return null;
    }
  }, [auth, checkPermission, getItem, isMainBand, R.text]);

  const handleMenuClick = useCallback(({ keyPath }) => {
    history.push("/app/" + keyPath[0]);
  }, [history]);

  return (
    <Drawer
      title={R.text.menu}
      placement={"left"}
      visible={open}
      onClose={onClose}
    >
      <Menu
        onClick={handleMenuClick}
        items={menuItems}
        mode={"inline"}
      />
    </Drawer>
  );
}

export default React.memo(Sidebar);
